import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { EHotelServiceRequestType, UserRole, VendorTypeService } from "../constants";
import { EServiceRequestType } from "../types/global.types";
import axios, { AxiosError } from "axios";

/**
 * Retrieves user data from the session storage.
 *
 * @return {any} The decoded user data if a token is present in the session storage, otherwise null.
 */
export const getUserData = (): any => {
  try {
    const token = sessionStorage.getItem("token");
    if (token) {
      const payload = jwtDecode(token);

      if (payload?.sub && !payload.iss?.includes("cognito-idp")) {
        (payload as any)["cognito:username"] = payload.sub;

        let roleName = "";
        (payload as any)?.groups?.some((groupId: string) => {
          if (groupId === process.env.REACT_APP_ENTRA_SUP_ADMIN_GROUP_ID) {
            roleName = UserRole.SUPER_ADMIN;
            return true;
          }
          if (groupId === process.env.REACT_APP_ENTRA_SHIP_HR_GROUP_ID) {
            roleName = UserRole.VENDOR;
            return true;
          }

          return false;
        });
        (payload as any)["custom:roleName"] = roleName;

        if (roleName === UserRole.VENDOR) {
          (payload as any)["custom:vendorType"] =
            VendorTypeService.SHIPBOARD_HR;
        }
      }

      return payload;
    }
  } catch (err) {
    console.error("JWT", err);
  }

  return null;
};

/**
 * Converts an ISO date string to a custom format.
 *
 * @param {string} isoDate - The ISO date string to be formatted.
 * @return {string} The formatted date string.
 */
export const formatDateToCustomFormat = (isoDate: string): string => {
  if (!isoDate) return "";

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const formattedDate = new Date(isoDate).toLocaleDateString(
    undefined,
    options
  );
  return formattedDate;
};

export const formatVersion = (version: number): string => {
  let [major, minor] = (version + "").split(".");

  return `${major}.${minor || 0}`;
};

export const timeOnly = (dateString: string): string => {
  return dayjs(dateString, "MM/DD/YYYY HH:mm:ss").format("HH:mm:ss");
};

export const dateOnly = (dateString: string) =>
  dayjs(dateString, "MM/DD/YYYY HH:mm:ss").format("MM/DD/YYYY");

export const flattenObject = (obj: any, parentKey = ""): any => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // skip id keys at any nesting level
    if (key === "id" || key === "provide_shuttle_service") return acc;

    const newKey = parentKey ? `${parentKey}_${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value, newKey));
    } else {
      acc[newKey] = value;
    }

    return acc;
  }, {} as any);
};

export const unflattenObject = (obj: any): any => {
  const result: any = {};

  Object.entries(obj).forEach(([key, value]) => {
    const keys = key.split("_");
    let current = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const currentKey = keys[i];

      if (['__proto__', 'constructor', 'prototype'].includes(currentKey)) {
        return;
      }

      if (!current[currentKey]) {
        current[currentKey] = {};
      }
      current = current[currentKey];
    }

    const lastKey = keys[keys.length - 1];
    if (['__proto__', 'constructor', 'prototype'].includes(lastKey)) {
      return;
    }
    current[lastKey] = value;
  });

  return result;
};


export const serviceRequestTypeToLabelMap: Record<EServiceRequestType, string> = {
  [EServiceRequestType.HOTEL_TO_AIRPORT]: 'Hotel to Airport',
  [EServiceRequestType.HOTEL_TO_PORT]: 'Hotel to Port',
  [EServiceRequestType.PORT_TO_AIRPORT]: 'Port to Airport',
  [EServiceRequestType.PORT_TO_HOTEL]: 'Port to Hotel',
  [EServiceRequestType.AIRPORT_TO_HOTEL]: 'Airport to Hotel',
  [EServiceRequestType.AIRPORT_TO_PORT]: 'Airport to Port',
  [EServiceRequestType.MEET_AND_GREET]: 'Meet & Greet',
  [EServiceRequestType.HOTEL]: 'Hotel Checkin',
  [EServiceRequestType.SHIPBOARD_TM_ONBOARDING]: 'Shipboard TM Onboarding',
};

export const hotelServiceRequestTypeToLabelMap: Record<EHotelServiceRequestType, string> = {
  [EHotelServiceRequestType.BREAKFAST]: 'Breakfast',
  [EHotelServiceRequestType.LUNCH]: 'Lunch',
  [EHotelServiceRequestType.DINNER]: 'Dinner',
};

export const getFormattedTravelApiErrorMessage = (detailedError?: AxiosError<any> | null) => {
  if (!detailedError || !axios.isAxiosError(detailedError)) return null;

  const axiosError = detailedError as any;

  if (axiosError?.code === "ERR_NETWORK" || axiosError?.message === "Network Error") {
    return "Unable to connect to the server. Please check your internet connection and try again.";
  }

  if (axiosError?.code === "ECONNABORTED" || axiosError?.message?.includes("timeout")) {
    return "The server took longer than expected to respond, and the request timed out. Please retry shortly or reduce the date range to proceed.";
  }

  const status = axiosError?.response?.status;
  if (status >= 500 && status < 600) {
    return "The server encountered an error. Please try again later.";
  }

  if (status === 401) {
    return "Your session has expired. Please log in again to continue.";
  }

  return "We are experiencing issues retrieving team member data at the moment. Please try refreshing the page, or check back again shortly.";
}